import { Grid, Typography } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import React, { useState, useRef, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ArrowBackIos as ArrowBackIosIcon } from '@material-ui/icons';
import MapTextField from 'components/Global/MapTextField';
import CustomButton from 'components/CustomInput/Button/index';
import MsgBox from 'components/MsgBox/MsgBox';
import API from 'api/call';
import { setGlobalLoading } from 'store/global/reducer';
import { GET_EMAIL_DOMAIN_LIST } from 'store/key';
import { checkIsAuthorizedEmail, checkIsFormattedHospital } from 'utility/checkValidations';

const useStyles = makeStyles((theme) => ({
    item: {
        padding: '12px 12px 0 !important',
    },
}));

export default function SignUpLayout(props) {
    const classes = useStyles();
    const dispatch = useDispatch();

    const { setPageComponent, showLoginLayout } = props;
    // const system = useSelector((state) => state.system);
    const systemMessage = useSelector((state) => state.systemMessage.systemMessage);
    // const formattedHospitalData = system.formattedHospitalData;

    const [warningMsg, setWarningMsg] = useState(null);

    const [formData, setFormData] = useState({
        email: {
            value: '',
            label: 'Email',
            name: 'email',
            type: 'input',
        },
        title: {
            value: '',
            label: 'Title',
            name: 'title',
            type: 'input',
        },
        firstName: {
            value: '',
            label: 'First Name',
            name: 'firstName',
            type: 'input',
        },
        surname: {
            value: '',
            label: 'Surname',
            name: 'surname',
            type: 'input',
        },
        hospitalOrInstitution: {
            value: '',
            label: 'Hospital / Institution',
            name: 'hospitalOrInstitution',
            type: 'hospitalOrInstitution',
        },
        contactNumber: {
            value: '',
            label: 'Contact Number',
            name: 'contactNumber',
            type: 'input',
        },
        password: {
            value: '',
            label: 'Password',
            name: 'password',
            type: 'password',
        },
        confirmPassword: {
            value: '',
            label: 'Confirm Password',
            name: 'confirmPassword',
            type: 'password',
        },
        justification: {
            value: '',
            label: 'Justification *',
            name: 'justification',
            type: 'input',
            helperText: '*Please give justification for using non-HA authorized email address',
        },
    });
    const [checkAuthorizedEmail, setCheckAuthorizedEmail] = useState(false);
    const [forceShowError, setForceShowError] = useState(false);
    const formDataValid = useRef({
        email: true,
        title: true,
        firstName: true,
        surname: true,
        hospitalOrInstitution: true,
        contactNumber: true,
        password: true,
        confirmPassword: true,
        justification: true,
    });

    const getEmailDomains = () => {
        dispatch({
            type: GET_EMAIL_DOMAIN_LIST,
            payload: null,
        });
    };

    useEffect(() => {
        getEmailDomains();
        // eslint-disable-next-line
    }, []);

    const checkFormDataValid = () => {
        let isValid = true;
        for (let i in formData) {
            const error =
                (!formData[i].value && i !== 'justification' && checkAuthorizedEmail) ||
                (!formData[i].value && !checkAuthorizedEmail);
            if (error) {
                isValid = false;
                break;
            }
        }
        const list = formDataValid.current;
        for (let i in list) {
            const error =
                (!list[i] && i !== 'justification' && checkAuthorizedEmail) || (!list[i] && !checkAuthorizedEmail);
            if (error) {
                isValid = false;
                break;
            }
        }
        return isValid;
    };

    const handleFormDataChange = (e) => {
        const { name, value } = e.target;
        const copyFormData = JSON.parse(JSON.stringify(formData));
        if (name === 'email') {
            const isAuthorizedEmail = checkIsAuthorizedEmail(value);
            /** reset hospitalOrInstitution,value = '' when not found hospitalName && hospitalCode from formattedHospitalData*/
            if (isAuthorizedEmail && !checkIsFormattedHospital(copyFormData.hospitalOrInstitution.value)) {
                copyFormData.hospitalOrInstitution.value = '';
            }
            setCheckAuthorizedEmail(isAuthorizedEmail);
        }
        copyFormData[name].value = value;
        setFormData(copyFormData);
    };

    const handleBackClick = () => {
        setPageComponent(showLoginLayout);
    };

    function handleSubmit(e) {
        e.preventDefault();
        setForceShowError(true);
        const valid = checkFormDataValid();
        if (valid) {
            setGlobalLoading(true);
            let requestData = {
                email: formData.email.value?.toLowerCase(),
                title: formData.title.value,
                firstName: formData.firstName.value,
                surname: formData.surname.value,
                institution: formData.hospitalOrInstitution.value,
                contact: formData.contactNumber.value,
                password: formData.password.value,
                confirmPassword: formData.confirmPassword.value,
            };
            if (!checkAuthorizedEmail) {
                requestData = {
                    ...requestData,
                    justification: formData.justification?.value,
                };
            }

            API.user
                .signUp(requestData)
                .then((res) => {
                    refleshFormData();
                    setWarningMsg({
                        type: 'confirm',
                        message: checkAuthorizedEmail
                            ? systemMessage.authorizedSignUpSuccess
                            : systemMessage.nonAuthorizedSignUpSuccess,
                        footerActions: [
                            {
                                label: 'Confirm',
                                clicked: () => {
                                    setWarningMsg(null);
                                    handleBackClick();
                                },
                            },
                        ],
                    });
                })
                .finally(() => {
                    setGlobalLoading(false);
                });
        }
    }

    const refleshFormData = () => {
        let copyFromData = JSON.parse(JSON.stringify(formData));
        for (let i in copyFromData) {
            copyFromData[i].value = '';
        }
        setFormData(copyFromData);
    };

    return (
        <>
            {warningMsg && (
                <MsgBox
                    type={warningMsg.type}
                    message={warningMsg.message}
                    footerActions={warningMsg.footerActions}
                    title={warningMsg.title}
                />
            )}
            <Grid container spacing={3}>
                <Grid item xs={12} className={classes.item}>
                    <Typography variant="h4" style={{ fontSize: 18, fontWeight: 'bold' }}>
                        Sign Up
                    </Typography>
                </Grid>
                {Object.keys(formData).length
                    ? Object.keys(formData).map((i) => {
                          const item = formData[i];
                          const itemXs = ['firstName', 'surname'].includes(item.name) ? 6 : 12;
                          const show = item.name === 'justification' ? !checkAuthorizedEmail : true;
                          const helperText = item.name === 'justification' ? item.helperText : '';

                          const isNoSpace = item.name === 'email';
                          const isTrim = !['password', 'confirmPassword'].includes(item.name);

                          const confirmPasswordNotMatchPassword =
                              formData.confirmPassword.value !== formData.password.value;

                          const validators = ['isRequired', 'disableChinese'];
                          if (item.name === 'email') {
                              validators.push('isEmail');
                          } else if (item.name === 'password') {
                              validators.push('checkPassword');
                          } else if (item.name === 'confirmPassword') {
                              validators.push('checkConfirmPassword');
                          } else if (item.name === 'contactNumber') {
                              validators.push('disableAlphabet');
                          }

                          return show ? (
                              <Grid key={item.name} item xs={itemXs} className={classes.item}>
                                  <MapTextField
                                      isAuthorizedEmail={checkAuthorizedEmail}
                                      item={item}
                                      handleFormData={handleFormDataChange}
                                      isNoSpace={isNoSpace}
                                      isTrim={isTrim}
                                      isValidValue={(valid) => (formDataValid.current[item.name] = valid)}
                                      forceErrorDisplay={forceShowError}
                                      confirmPasswordNotMatchPassword={confirmPasswordNotMatchPassword}
                                      validators={validators}
                                      helperText={helperText}
                                      maxLength={255}
                                      showErrorWhenOnBlur={item.name === 'email'}
                                  />
                              </Grid>
                          ) : null;
                      })
                    : null}

                <Grid item xs={6}>
                    <CustomButton color="default" fullWidth startIcon={<ArrowBackIosIcon />} onClick={handleBackClick}>
                        Back
                    </CustomButton>
                </Grid>
                <Grid item xs={6}>
                    <CustomButton buttonType="submit" fullWidth onClick={handleSubmit}>
                        Submit
                    </CustomButton>
                </Grid>
            </Grid>
        </>
    );
}
